import { Component } from '@theme/component';
import { planPurchaseDock } from './vg-purchase-dock-model.js';

const PRESENTATION_PROPERTIES = ['reserve', 'end-reserve', 'min-height', 'left', 'width', 'top', 'held-top'].map(name => `--vg-dock-${name}`);

const box = element => {
  if (!element?.isConnected || element.closest('[hidden]')) return null;
  const style = getComputedStyle(element);
  const r = element.getBoundingClientRect();
  if (style.display === 'none' || style.visibility !== 'visible' || Number(style.opacity) === 0 || !r.width || !r.height) return null;
  return { x: r.x, y: r.y, width: r.width, height: r.height };
};

// Shopify's preview iframe can span the viewport while its fixed wrapper clips
// it to the visible bar. Measure that clip, including the expanded menu state.
const previewBox = (frame, wrapper) => {
  const rect = box(frame);
  if (!rect || !wrapper) return rect;
  const style = getComputedStyle(wrapper);
  // Unknown layouts keep the conservative iframe rectangle. A fixed child can
  // escape an ancestor's overflow clip; the observed preview child is absolute.
  if (getComputedStyle(frame).position !== 'absolute' || style.position !== 'fixed') return rect;
  if (style.display === 'none' || Number(style.opacity) === 0) return null;
  // A zero-size overflow-visible wrapper does not hide its child. Apply zero
  // dimensions only on axes that actually clip; visibility can be overridden.
  const bounds = wrapper.getBoundingClientRect();
  const clips = value => /^(hidden|clip|auto|scroll)$/.test(value);
  const clipX = clips(style.overflowX || style.overflow);
  const clipY = clips(style.overflowY || style.overflow);
  const left = clipX ? Math.max(rect.x, bounds.x) : rect.x;
  const right = clipX ? Math.min(rect.x + rect.width, bounds.x + bounds.width) : rect.x + rect.width;
  const top = clipY ? Math.max(rect.y, bounds.y) : rect.y;
  const bottom = clipY ? Math.min(rect.y + rect.height, bounds.y + bounds.height) : rect.y + rect.height;
  return right > left && bottom > top ? { x: left, y: top, width: right - left, height: bottom - top } : null;
};

/** Read only open, rendered DOM. Never use the full-viewport chat host as a hitbox. */
export function measureLaunchers(root = document) {
  const roots = [];
  const visit = scope => {
    roots.push(scope);
    for (const element of scope.querySelectorAll('*')) if (element.shadowRoot) visit(element.shadowRoot);
  };
  const chat = root.querySelector('shopify-chat');
  if (chat?.shadowRoot) visit(chat.shadowRoot);
  const activator = roots.flatMap(scope => [...scope.querySelectorAll('[part~="activator"]')]).find(node => box(node));
  const chatRect = box(activator);
  const chatOpen = roots.some(scope => [...scope.querySelectorAll('[part~="dialog"]')].some(node => box(node)));
  const smileHost = root.querySelector('#smile-ui-container');
  const lite = root.querySelector('#smile-lite-launcher-frame');
  const rich = root.querySelector('#smile-ui-container .smile-launcher-frame-container');
  const richFrame = rich?.querySelector('iframe');
  const smileRect = box(lite) || (rich?.classList.contains('smile-launcher-closed') ? box(richFrame) : null);
  const smileOpen = Boolean(rich && !rich.classList.contains('smile-launcher-closed') && box(richFrame)) ||
    [...root.querySelectorAll('#smile-ui-container iframe')].some(frame => !rich?.contains(frame) && box(frame));
  const preview = root.querySelector('#PBarNextFrame');
  const previewWrapper = preview?.closest('#PBarNextFrameWrapper');
  const previewRect = previewBox(preview, previewWrapper);
  const panelOpen = chatOpen || smileOpen || [...root.querySelectorAll('dialog[open], details.vg-mobile-menu[open], details.vg-search-toggle[open]')].some(node => box(node));
  return {
    measurementsKnown: (!chat || Boolean(chatRect) || chatOpen)
      && (!(lite || rich || smileHost) || Boolean(smileRect) || smileOpen),
    panelOpen,
    obstructionRects: [chatRect, smileRect, previewRect].filter(Boolean),
    observed: [chat, activator, lite, richFrame, preview, previewWrapper].filter(Boolean),
    shadowRoots: roots,
  };
}

/** CSS-only positioning of an existing control group; owns no purchasing state. */
export class VgPurchaseDock extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.dispose();
    this.controller = new AbortController();
    this.control = this.firstElementChild;
    if (!this.control?.matches('vg-product-purchase, vg-cart-checkout, [data-vg-dock-group]')) return;
    this.control.dataset.vgDockControl = '';
    const options = { signal: this.controller.signal, passive: true };
    const schedule = () => this.schedule();
    for (const type of ['scroll', 'resize', 'pageshow']) window.addEventListener(type, schedule, options);
    window.visualViewport?.addEventListener('resize', schedule, options);
    window.visualViewport?.addEventListener('scroll', schedule, options);
    this.addEventListener('focusin', () => this.refresh(), options);
    this.addEventListener('focusout', schedule, options);
    this.addEventListener('pointerdown', () => { this.pressed = true; this.refresh(); }, options);
    for (const type of ['pointerup', 'pointercancel']) window.addEventListener(type, () => { this.pressed = false; this.schedule(); }, options);
    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this.control);
    this.resizeTargets = new Set([this.control]);
    this.mutationObserver = new MutationObserver(records => {
      if (records.some(record => !this.contains(record.target) || record.attributeName !== 'style'
        || record.target === this && this.presentation && PRESENTATION_PROPERTIES.some(name => this.style.getPropertyValue(name) !== this.presentation.styles[name]))) this.schedule();
    });
    this.mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'style', 'open', 'aria-expanded'] });
    this.shadowObservers = new Map();
    // A bounded fallback discovers late open-shadow app roots and transform-only changes.
    this.timer = window.setInterval(schedule, 500);
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.dispose();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.control !== this.firstElementChild) {
      this.reset();
      this.connectedCallback();
    } else if (this.control?.matches('vg-product-purchase, vg-cart-checkout, [data-vg-dock-group]')) {
      // Theme morphing removes attributes absent from server markup even when
      // it retains the same form nodes. Restore presentation ownership only.
      this.control.dataset.vgDockControl = '';
      this.refresh();
    }
  }

  dispose() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.resizeTargets?.clear();
    this.mutationObserver?.disconnect();
    for (const observer of this.shadowObservers?.values() || []) observer.disconnect();
    this.shadowObservers?.clear();
    window.clearInterval(this.timer);
    this.timer = null;
    window.cancelAnimationFrame(this.frame);
    this.frame = null;
    this.pressed = false;
  }

  schedule() {
    if (!this.isConnected || this.controller?.signal.aborted || this.frame != null) return;
    this.frame = window.requestAnimationFrame(() => { this.frame = null; this.refresh(); });
  }

  /** Reconcile app subscriptions without reconnecting the native purchase group. */
  syncObservedTargets({ observed = [], shadowRoots = [] } = {}) {
    if (!this.resizeObserver || !this.resizeTargets || !this.shadowObservers) return;
    const currentTargets = new Set([this.control, ...observed].filter(node => node?.isConnected));
    for (const node of this.resizeTargets) if (!currentTargets.has(node)) {
      this.resizeObserver.unobserve(node);
      this.resizeTargets.delete(node);
    }
    for (const node of currentTargets) if (!this.resizeTargets.has(node)) {
      this.resizeObserver.observe(node);
      this.resizeTargets.add(node);
    }
    const currentRoots = new Set(shadowRoots.filter(root => root.host?.isConnected));
    for (const [root, observer] of this.shadowObservers) if (!currentRoots.has(root)) {
      observer.disconnect();
      this.shadowObservers.delete(root);
    }
    for (const root of currentRoots) if (!this.shadowObservers.has(root)) {
      const observer = new MutationObserver(() => this.schedule());
      observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'style', 'open', 'aria-expanded'] });
      this.shadowObservers.set(root, observer);
    }
  }

  refresh() {
    try { this.updateLayout(); }
    finally {
      this.presentation = {
        mode: this.dataset.vgDockMode,
        styles: Object.fromEntries(PRESENTATION_PROPERTIES.map(name => [name, this.style.getPropertyValue(name)])),
      };
    }
  }

  updateLayout() {
    if (!this.isConnected || !this.control) return;
    if (this.control !== this.firstElementChild) {
      this.reset();
      this.presentation = null;
      this.connectedCallback();
      return;
    }
    // The theme's morph callback can target the detached response element.
    // Recover presentation here too; never depend on a purchase-state callback.
    this.control.dataset.vgDockControl = '';
    if (!this.hasAttribute('data-vg-dock-mode') && this.presentation?.mode) {
      for (const [name, value] of Object.entries(this.presentation.styles)) {
        if (value) this.style.setProperty(name, value);
      }
      this.dataset.vgDockMode = this.presentation.mode;
    }
    const visible = !this.closest('[hidden]') && box(this);
    const scope = this.closest('.vg-native-product, .vg-native-cart');
    if (!visible || !scope || this.closest('.vg-dialog')) {
      this.syncObservedTargets();
      this.reset();
      return;
    }
    const measured = measureLaunchers();
    this.syncObservedTargets(measured);
    const viewport = window.visualViewport;
    const width = document.documentElement.clientWidth;
    const height = document.documentElement.clientHeight;
    const zoomOrKeyboard = viewport && (Math.abs(viewport.scale - 1) > .01 || viewport.height < height - 1 || Math.abs(viewport.offsetTop) > 1);
    const region = this.getBoundingClientRect();
    const control = this.control.getBoundingClientRect();
    const active = this.contains(document.activeElement) || this.pressed;
    // Preserve the focused/clicked group's screen position at the transition, in its own slot.
    // It then scrolls naturally; it never remains fixed over unrelated content.
    if (active && this.dataset.vgDockMode === 'docked') {
      this.style.setProperty('--vg-dock-held-top', `${Math.max(0, control.top - region.top)}px`);
      this.dataset.vgDockMode = 'held';
    }
    if (active) {
      if (this.dataset.vgDockMode === 'held') {
        const heldTop = Number.parseFloat(this.style.getPropertyValue('--vg-dock-held-top')) || 0;
        const current = Number.parseFloat(this.style.getPropertyValue('--vg-dock-min-height')) || 0;
        this.style.setProperty('--vg-dock-min-height', `${Math.max(current, heldTop + control.height + 8)}px`);
      }
      return;
    }
    const plan = planPurchaseDock({
      viewportRect: { x: 0, y: 0, width, height },
      controlHeight: control.height,
      regionRect: { x: region.x, y: region.y, width: region.width, height: region.height },
      ...measured,
      panelOpen: measured.panelOpen || Boolean(zoomOrKeyboard),
    });
    this.dataset.vgDockReason = plan.reason;
    if (plan.mode === 'inactive') { this.reset(); return; }
    if (plan.mode === 'suspended') {
      // Retain established flow space; do not infer missing widgets have zero size.
      this.dataset.vgDockMode = 'inline';
      return;
    }
    const { paddingBlockStart, paddingBlockEnd = 0, minBlockSize } = plan.reservation;
    const nextPadding = `${paddingBlockStart}px`;
    const changed = this.style.getPropertyValue('--vg-dock-reserve') !== nextPadding;
    this.style.setProperty('--vg-dock-reserve', nextPadding);
    this.style.setProperty('--vg-dock-end-reserve', `${paddingBlockEnd}px`);
    this.style.setProperty('--vg-dock-min-height', `${minBlockSize}px`);
    this.style.setProperty('--vg-dock-left', `${region.left}px`);
    this.style.setProperty('--vg-dock-width', `${region.width}px`);
    this.style.setProperty('--vg-dock-top', `${plan.dockRect.y}px`);
    this.dataset.vgDockMode = plan.mode === 'docked' && !changed ? 'docked' : 'inline';
    if (changed || plan.mode === 'reserve') this.schedule();
  }

  reset() {
    this.dataset.vgDockMode = 'inline';
    for (const name of PRESENTATION_PROPERTIES) this.style.removeProperty(name);
  }
}

if (!customElements.get('vg-purchase-dock')) customElements.define('vg-purchase-dock', VgPurchaseDock);
